import React, { useCallback, useContext, useEffect, useMemo } from 'react';

import { Box, Container, Typography } from '@mui/material';
import { history as instantSearchHistory } from 'instantsearch.js/cjs/lib/routers';
import { useTranslation } from 'react-i18next';
import { InstantSearch } from 'react-instantsearch-hooks-web';
import { useLocation } from 'react-router-dom';

import { Header } from '../../../../components/Header';
import { useCommonHeaderStyles } from '../../../../components/Header/style';
import { Loader } from '../../../../components/Loader';
import SearchBox from '../../../../components/SearchBox/SearchBox';
import { DiscoverContext } from '../../../../context/DiscoverContext';
import useScriptLoader, { SCRIPTS } from '../../../../context/ScriptLoader/useScriptLoader';
import { DiscoverRoutes } from '../../../../routes';
import { Tenant } from '../../../../types';
import {
  HEADER_IMAGE_GLOSSARY,
  HEADER_IMAGE_INSIGHTS,
  HEADER_IMAGE_PATHWAYS,
  HEADER_IMAGE_VIDEOS,
} from '../../../../utils/const';
import { generateRandomImageUrl } from '../../../../utils/helpers';
import { useSERPNavigation } from '../../../../utils/hooks';
import Breadcrumbs from '../../components/Breadcrumbs';
import { ControlledTabs } from '../../components/ControlledTabs';
import SERPContainer from '../../components/SERPContainer';
import { SearchSeoBlock } from '../../components/SearchSeoBlock';
import { Tabs } from '../../components/Tabs';
import { FILTERS, HITS_TYPES } from '../../const';
import { getNavigationArray } from './helpers';
import useStyles from './styles';

const algoliaParams = ['s', 'refinementList', 'hierarchicalMenu', 'menu'];

const convertAllKeysToArr = (obj) => {
  const res = {};

  if (obj) {
    for (const [key, value] of Object.entries(obj)) {
      if (value) {
        res[key] = Array.isArray(value) ? value : [value];
      }
    }
  }

  return res;
};

interface SearchPageProps {
  tenant: Tenant;
  algoliaIndexName: string;
  searchClient: any;
  loadingKey: boolean;
  tenantName?: string | null;
  userLoading: boolean;
}

const SearchImagesMap = {
  [DiscoverRoutes.Videos]: HEADER_IMAGE_VIDEOS,
  [DiscoverRoutes.Pathways]: HEADER_IMAGE_PATHWAYS,
  [DiscoverRoutes.Glossary]: HEADER_IMAGE_GLOSSARY,
  [DiscoverRoutes.PathwaysDirect]: HEADER_IMAGE_PATHWAYS,
  [DiscoverRoutes.VideosDirect]: HEADER_IMAGE_VIDEOS,
  [DiscoverRoutes.Insights]: HEADER_IMAGE_INSIGHTS,
};

const SearchPage: React.FC<SearchPageProps> = ({
  tenant,
  algoliaIndexName,
  searchClient,
  loadingKey,
  tenantName,
  userLoading,
}) => {
  useScriptLoader(SCRIPTS.TEAMS_SHARE);
  const { t } = useTranslation();
  const headerClasses = useCommonHeaderStyles();
  const location = useLocation();

  const { searchState, setSearchState } = useContext(DiscoverContext);

  const type = useSERPNavigation();
  const classes = useStyles(!searchState?.query)();

  const imageByType = SearchImagesMap[location.pathname];
  //used to prevent changing header img while switching between tabs
  const defaultImage = useMemo(() => generateRandomImageUrl(tenantName), [tenantName]);
  const headerImage = imageByType || defaultImage;

  const SearchPlaceholdersMap = {
    [DiscoverRoutes.Videos]: t('page.discover.searchAllVideoModules'),
    [DiscoverRoutes.Pathways]: t('page.discover.searchAllPathways'),
    [DiscoverRoutes.Glossary]: t('page.discover.searchGlossary'),
    [DiscoverRoutes.PathwaysDirect]: t('page.discover.searchAllPathways'),
    [DiscoverRoutes.VideosDirect]: t('page.discover.searchAllVideoModules'),
    [DiscoverRoutes.Insights]: t('page.discover.searchAllInsights'),
  };
  const searchPlaceholder = SearchPlaceholdersMap[location.pathname];

  const initialUiState = {
    [algoliaIndexName]: {
      refinementList: {
        [FILTERS.SearchType]: type ? [type] : [],
      },
    },
  };

  const routing = useMemo(
    () => ({
      router: instantSearchHistory({
        createURL({ qsModule, routeState, location }) {
          const params = qsModule.parse(location?.search?.slice(1));

          let queryStringParams = {};

          for (const [key, value] of Object.entries(params)) {
            if (!algoliaParams.includes(key)) {
              queryStringParams[key] = value;
            }
          }

          queryStringParams = {
            ...queryStringParams,
            ...routeState,
          };

          const queryString = qsModule.stringify(queryStringParams, {
            addQueryPrefix: true,
            arrayFormat: 'repeat',
          });

          return `${location.origin}${location.pathname}${queryString}`;
        },
        parseURL({ qsModule, location }) {
          const params = qsModule.parse(location?.search?.slice(1));

          const { s, refinementList, hierarchicalMenu, menu } = params;

          const newRefinementList = convertAllKeysToArr(refinementList);
          const newHierarchicalMenu = convertAllKeysToArr(hierarchicalMenu);

          return {
            s,
            menu,
            ...(refinementList && { refinementList: newRefinementList }),
            ...(hierarchicalMenu && { hierarchicalMenu: newHierarchicalMenu }),
          };
        },
      }),
      stateMapping: {
        stateToRoute(uiState) {
          //to prevent warning about unused configure and sortBy
          // eslint-disable-next-line
          const { query, configure, sortBy, refinementList, ...filters } =
            uiState[algoliaIndexName];

          //eslint-disable-next-line
          const { searchable_type, ...rest } = refinementList || {};

          return {
            s: query,
            ...(refinementList && {
              refinementList: {
                ...rest,
                ...(!type &&
                  refinementList[FILTERS.SearchType] && {
                    [FILTERS.SearchType]: refinementList[FILTERS.SearchType],
                  }),
              },
            }),
            ...filters,
          };
        },
        routeToState(routeState) {
          const { s, ...filters } = routeState;

          return {
            [algoliaIndexName]: {
              query: s,
              ...filters,
            },
          };
        },
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [algoliaIndexName, type, location.search],
  );

  const navigationArr = getNavigationArray(type);

  const onChange = useCallback(
    ({ uiState, setUiState }) => {
      const { refinementList, ...rest } = uiState?.[algoliaIndexName];

      const updatedState = {
        ...rest,
        refinementList: {
          ...refinementList,
          ...(type && { [FILTERS.SearchType]: [type] }),
        },
      };

      setUiState({
        [algoliaIndexName]: updatedState,
      });
      setSearchState(updatedState);
    },
    [type, algoliaIndexName, setSearchState],
  );

  useEffect(() => {
    return () => setSearchState({});
  }, [setSearchState]);

  return (
    <>
      <SearchSeoBlock tenantTitle={tenant?.title} tenantName={tenant?.name} />
      <InstantSearch
        onStateChange={onChange}
        searchClient={searchClient}
        indexName={algoliaIndexName}
        routing={routing}
        initialUiState={initialUiState}
      >
        {loadingKey ? (
          <Loader />
        ) : (
          <>
            <Header
              wrapperClass={classes.header}
              title={t('page.discover.discover')}
              tenantName={tenantName}
              imageUrl={headerImage}
            >
              <Container className={headerClasses.centeredContainer}>
                <SearchBox withClear={true} placeholder={searchPlaceholder} />
                {type === '' ? (
                  <Tabs selectedItem={''} />
                ) : (
                  <ControlledTabs attribute={FILTERS.SearchType} withLinks />
                )}
              </Container>
            </Header>
            <Container data-testid="searchPage">
              <Breadcrumbs crumbs={navigationArr} />
              <Box className={classes.descriptionContainer}>
                {type === HITS_TYPES.VIDEO && (
                  <Box className={headerClasses.wrapperWithBorder}>
                    <Typography
                      data-testid="tabContentTitle"
                      variant="h2"
                      className={headerClasses.searchTitle}
                    >
                      {t('page.discover.videoModules')}
                    </Typography>
                    <Typography
                      data-testid="tabContentDescription"
                      className={headerClasses.searchDescription}
                    >
                      {t('page.discover.videosDescription')}
                    </Typography>
                  </Box>
                )}
                {type === HITS_TYPES.PATHWAY && (
                  <Box className={headerClasses.wrapperWithBorder}>
                    <Typography
                      data-testid="tabContentTitle"
                      variant="h2"
                      className={headerClasses.searchTitle}
                    >
                      {t('page.discover.pathways')}
                    </Typography>
                    <Typography
                      data-testid="tabContentDescription"
                      className={headerClasses.searchDescription}
                    >
                      {t('page.discover.pathwaysDescription')}
                    </Typography>
                  </Box>
                )}
                {type === HITS_TYPES.NEWS && (
                  <Box className={headerClasses.wrapperWithBorder}>
                    <Typography
                      data-testid="tabContentTitle"
                      variant="h2"
                      className={headerClasses.searchTitle}
                    >
                      {t('page.discover.text')}
                    </Typography>
                    <Typography
                      data-testid="tabContentDescription"
                      className={headerClasses.searchDescription}
                    >
                      {t('page.discover.textDescription')}
                    </Typography>
                  </Box>
                )}
                {type === HITS_TYPES.GLOSSARY && (
                  <Box className={headerClasses.wrapperWithBorder}>
                    <Typography
                      data-testid="tabContentTitle"
                      variant="h2"
                      className={headerClasses.searchTitle}
                    >
                      {t('page.discover.glossary')}
                    </Typography>
                    <Typography
                      data-testid="tabContentDescription"
                      className={headerClasses.searchDescription}
                    >
                      {t('page.discover.glossaryDescription')}
                    </Typography>
                  </Box>
                )}
              </Box>
              <SERPContainer
                tenantTitle={tenant?.title}
                tenantName={tenant?.name}
                linkedInId={tenant?.linkedin_id}
                attributesExcludedFromClear={type ? [FILTERS.SearchType] : []}
                loadingApiKey={loadingKey}
                userLoading={userLoading}
              />
            </Container>
          </>
        )}
      </InstantSearch>
    </>
  );
};

export default SearchPage;
